import {apiProtocol, contentHost, apiDomain} from 'settings'
import {clsx, styled ,Fade , Box ,Paper, AssignmentTurnedInIcon, AttachFileIcon, ScheduleIcon } from 'external/material'
import {useEffect} from 'external/react'
import {isAfter} from 'external/date-fns'

import {contentLink, viewerGuard} from 'utils/url'

import {resetUploads, loadUploads} from 'components/Uploads/redux/actions'
import uploadStore from 'components/Uploads/redux/store'
import UploadFiles from 'components/Uploads/UploadFiles'

import useTranslations from 'hooks/translations'
import useHomework from 'hooks/homework'
import useProfile from 'hooks/profile'
import useStudent from 'hooks/student'
import useValidations from 'hooks/validations'
import useForm from 'hooks/form'

import validations from './validations'
import initialState from './state'
import homeworkStyles from './styles'
import HomeworkHistory from './HomeworkHistory'

const Container = styled(Paper)(homeworkStyles)

export default function Homework() {
  const {getTeacherPassport} = useStudent()
  const {passport} = useProfile()
  const homeworks = useHomework()
  const {translate, formatDate} = useTranslations()
  const {homeworkLoaded: loaded} = homeworks 
  const homework = loaded ? homeworks.homework.homework : {}
  const status = loaded && homeworks.homework.status
  const studentHomework = loaded ? homeworks.homework : {}
  const {errors, validate} = useValidations(validations)
  const {state, setState} = useForm(initialState)

  const {endDate, statusName, latestStatus = {}, properties} = homework

  const hideTurnedIn = ['completed', 'pending'].indexOf(statusName) < 0

  const teacherPassport = getTeacherPassport(homework.teacherId)

  function sendLoadUploads() {
    uploadStore.dispatch(resetUploads())
    loaded && studentHomework.properties.files && uploadStore.dispatch(loadUploads(studentHomework.properties.files))
  }

  useEffect(() => { validate(state) }, [state])

  useEffect(()=> {
    if(!homework.id) return;
    setState({...state, folderId: homework.id})
    sendLoadUploads()
  }, [homework])
  
  const expiredHomework = isAfter(new Date(), endDate)
  const readOnlyHomework = expiredHomework || ['assigned', 'returned'].indexOf(homework.statusName) < 0

  return (
  <Fade in={loaded}>
    <Container>
      <Box className="content">
        <Box className="dates">                  
          <Field type="date" label={translate('homework-to-date')}><ScheduleIcon />{loaded && formatDate(endDate)}</Field>
          <Field type="date" label={translate('turnedIn-header')} classes ={[{hide: hideTurnedIn}]}>
            <AssignmentTurnedInIcon style={{fill: 'light-green', marginRight: 5}} />
            {latestStatus.created && formatDate(latestStatus.created)}
          </Field> 
          <Field type="score" label={translate('score')}>{loaded && `${latestStatus.score??' - '} / ${properties.score}`}</Field>
          <Field type="status" label={translate('status')} contentClasses={['status', `${homework.statusName}`]}>
            {loaded && translate(`homework-status-${homework.statusName}`)}
          </Field>
          <div className="break" />
          <Field type="instructions" label={translate('instructions')}>{loaded && properties.instructions}</Field>
        </Box>
        <Box className="flexWrap">
          <Box className="leftColumn">
            {(loaded && properties.files && properties.files.length > 0) && (
              <Box className="resources">
                <label>{translate('homework-material')}</label>
                <hr />
                {properties.files.map((f, i)=> (                 
                  <div key={i}>
                    <AttachFileIcon />
                    {<a {...viewerGuard({href: contentLink(teacherPassport, f.folder, f.name), target: "_blank"})}>{f.name}</a>}
                  </div> 
                ))}
              </Box>)}
          </Box>
          <Box className="rightColumn">
            <Box className="myfiles">
              <label>{translate('homework-student-files')}</label>
              <hr />
              <label htmlFor='homeworkInstructions'>{translate(errors.instructions)}</label>
              <Box className="myfiles-container">
                <UploadFiles 
                  readOnly={readOnlyHomework}
                  server={`${apiProtocol}://${contentHost}.${apiDomain}/files`}
                  folder={`homeworks/${homework.id}`}
                  downloadServer={contentLink(passport.id)} 
                  translate={translate} 
                  buttonText={translate('homework-upload-file')}
                />
              </Box>
            </Box>    
          </Box>  
        </Box>
        <Box className={clsx("comments", {hide: !status})}>
          <Box style={{fontWeight: 'bold', borderBottom: '1px solid #e8e8e8', marginBottom: 10}}>{translate('feedback-header')}</Box>
          {status && <HomeworkHistory status={status} />}
        </Box>
      </Box>
    </Container>
  </Fade>)
}

function Field(props) {
  const {label, classes = [], type, contentClasses = [], children} = props
  return (<Box className={clsx('field', ...classes)}>
    <label className={`${type}FieldLabel`}>{label}</label>
    <span className={clsx(`${type}Field`,...contentClasses)}>{children}</span>                
  </Box>)
}
